import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from 'assets/settings/components/DeleteIcon';
import EditIcon from 'assets/settings/components/EditIcon';
import Pagination from 'common-ui/table/Pagination';
import { getCountries } from 'features/newsales/newsalesActions';
import {
  selectCountries,
  selectCountriesCurrentPage,
  selectCountriesDeleteSuccess,
  selectCountriesLoading,
  selectCountriesPagesCount,
  selectCountriesSearchData,
  selectCountriesSuccess,
  selectCountriesUpdateSuccess,
  setCountriesCreateSuccess,
  setCountriesCurrentPage,
  setCountriesDeleteSuccess,
  setCountriesSearchData,
  setCountriesUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import {
  ActionsCell,
  EmptyState,
  IconButton,
  LoadingSpinner,
  PaginationWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderActions,
  TableRow,
} from '../Addresses.styles';

const CountryPage = ({ onEdit, onDelete, onAllCountriesChange }) => {
  const dispatch = useDispatch();

  const countries = useSelector(selectCountries);
  const countriesLoading = useSelector(selectCountriesLoading);
  const countriesCurrentPage = useSelector(selectCountriesCurrentPage);
  const countriesPagesCount = useSelector(selectCountriesPagesCount);
  const countriesSearchData = useSelector(selectCountriesSearchData);
  const countriesCreateSuccess = useSelector(selectCountriesSuccess);
  const countriesUpdateSuccess = useSelector(selectCountriesUpdateSuccess);
  const countriesDeleteSuccess = useSelector(selectCountriesDeleteSuccess);

  useEffect(() => {
    dispatch(getCountries({ limit: 1000 })).then((result) => {
      if (result.payload && result.payload.data) {
        onAllCountriesChange(result.payload.data);
      }
    });
  }, [dispatch, onAllCountriesChange]);

  useEffect(() => {
    dispatch(
      getCountries({
        page: countriesCurrentPage,
        limit: countriesSearchData.limit,
        search: countriesSearchData.search,
      })
    );
  }, [dispatch, countriesCurrentPage, countriesSearchData.limit, countriesSearchData.search]);

  useEffect(() => {
    if (countriesCreateSuccess) {
      dispatch(setCountriesCreateSuccess(false));
      dispatch(
        getCountries({
          page: countriesCurrentPage,
          limit: countriesSearchData.limit,
          search: countriesSearchData.search,
        })
      );
      dispatch(getCountries({ limit: 1000 })).then((result) => {
        if (result.payload && result.payload.data) {
          onAllCountriesChange(result.payload.data);
        }
      });
    }
  }, [
    countriesCreateSuccess,
    dispatch,
    countriesCurrentPage,
    countriesSearchData.limit,
    countriesSearchData.search,
    onAllCountriesChange,
  ]);

  useEffect(() => {
    if (countriesUpdateSuccess) {
      dispatch(setCountriesUpdateSuccess(false));
      dispatch(getCountries({ limit: 1000 })).then((result) => {
        if (result.payload && result.payload.data) {
          onAllCountriesChange(result.payload.data);
        }
      });
    }
  }, [countriesUpdateSuccess, dispatch, onAllCountriesChange]);

  useEffect(() => {
    if (countriesDeleteSuccess) {
      dispatch(setCountriesDeleteSuccess(false));
      if (countries?.length === 1 && countriesCurrentPage > 1) {
        dispatch(setCountriesCurrentPage(countriesCurrentPage - 1));
      } else {
        // Refetch current page data after deletion
        dispatch(
          getCountries({
            page: countriesCurrentPage,
            limit: countriesSearchData.limit,
            search: countriesSearchData.search,
          })
        );
      }
      dispatch(getCountries({ limit: 1000 })).then((result) => {
        if (result.payload && result.payload.data) {
          onAllCountriesChange(result.payload.data);
        }
      });
    }
  }, [
    countriesDeleteSuccess,
    dispatch,
    countries,
    countriesCurrentPage,
    countriesSearchData.limit,
    countriesSearchData.search,
    onAllCountriesChange,
  ]);

  const handlePageChange = (page) => {
    dispatch(setCountriesCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setCountriesSearchData({ limit: count, offset: 0 }));
    dispatch(setCountriesCurrentPage(1));
  };

  if (countriesLoading) {
    return <LoadingSpinner>Loading...</LoadingSpinner>;
  }

  if (!countries || countries.length === 0) {
    return <EmptyState>No countries found.</EmptyState>;
  }

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeaderActions>Actions</TableHeaderActions>
          </TableRow>
        </TableHead>
        <TableBody>
          {countries.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.name}</TableCell>
              <ActionsCell>
                <IconButton onClick={() => onEdit(item)}>
                  <EditIcon size={18} color="#2D6CDF" />
                </IconButton>
                <IconButton onClick={() => onDelete(item)}>
                  <DeleteIcon size={18} color="#FB2C36" />
                </IconButton>
              </ActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {countriesPagesCount > 1 && (
        <PaginationWrapper>
          <Pagination
            currentPage={countriesCurrentPage}
            totalPages={countriesPagesCount}
            onPageChange={handlePageChange}
            handleRowCountChange={handleRowCountChange}
            dataCount={countries.length}
            count={countriesSearchData.limit}
            editableRowCount
          />
        </PaginationWrapper>
      )}
    </>
  );
};

export default CountryPage;
